import * as L from 'leaflet';
import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SnackBarConfig } from '@shared/constants/snackbar-config';
import { CustomTextDialogComponent } from '../components/custom-text-dialog/custom-text-dialog.component';
import { take } from 'rxjs';
import { DialogService } from '@core/services/dialog.service';
import { DvSnackbarService } from '@devinco/ui/dds/snackbar';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { MapLayer } from '../interfaces/map';

@Injectable({ providedIn: 'root' })
export class ClipboardService {
  private translate = inject(TranslateService);
  private dialog = inject(DialogService);
  private snackbar = inject(DvSnackbarService);
  private location = inject(Location);
  private router = inject(Router);

  private dblClickMarker?: L.Marker;

  private readonly dblClickIcon = L.icon({
    iconUrl: './assets/map-marker-default.svg',
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });

  addMarkerToMap(
    map: L.Map,
    latlng: L.LatLng,
    text?: string,
    openPopupByDefault = true,
    isReadonlyPopup = false,
  ): void {
    if (this.dblClickMarker) {
      this.dblClickMarker.closePopup();
      map.removeLayer(this.dblClickMarker);
    }

    this.dblClickMarker = L.marker(latlng, {
      icon: this.dblClickIcon,
    }).addTo(map);

    (this.dblClickMarker as MapLayer).extraData = { origin: 'user', isSharedLinkPin: true };

    if (isReadonlyPopup && text) {
      this.dblClickMarker.bindPopup(text, { className: 'general-popup' });
    } else if (!isReadonlyPopup) {
      this.bindPopupToMarker(this.dblClickMarker, text);
    }

    if (openPopupByDefault) {
      this.dblClickMarker.openPopup();
    }
  }

  setupMapDblClickEvent(map: L.Map): void {
    map.on('contextmenu', (e: L.LeafletMouseEvent) => {
      const latlng = e.latlng;

      this.addMarkerToMap(map, latlng, undefined, false);

      if (!this.dblClickMarker) return;

      this.dblClickMarker.on('contextmenu', () => {
        this.openMarkerContextMenu();
      });

      this.updateUrlFromMap(map, this.dblClickMarker.getLatLng());

      this.dblClickMarker.on('popupopen', (popupEvent: L.PopupEvent) => {
        this.bindPopupActions(map, popupEvent.popup);
      });

      this.dblClickMarker.openPopup();
    });
  }

  private bindPopupActions(map: L.Map, popup: L.Popup): void {
    const popupEl = popup.getElement();
    if (!popupEl) return;

    const copyBtn = popupEl.querySelector('#vt-copy-link');
    const customTextBtn = popupEl.querySelector('#vt-custom-text');
    const removeMarker = popupEl.querySelector('#vt-remove-marker');

    copyBtn?.addEventListener('click', () => {
      this.copyUrlToClipboard();
      this.showCopiedToClipboardSnackbar();
      map.closePopup();
    });

    customTextBtn?.addEventListener('click', () => {
      this.openCustomTextDialog(this.dblClickMarker!.getLatLng(), map);
      map.closePopup();
    });

    removeMarker?.addEventListener('click', () => {
      if (this.dblClickMarker) {
        map.removeLayer(this.dblClickMarker);
        this.router.navigate([], {
          queryParamsHandling: 'replace',
          replaceUrl: true,
        });
      }
    });
  }

  private bindPopupToMarker(marker: L.Marker, text?: string): void {
    marker.bindPopup(
      `<div class="context-menu">${
        text
          ? `<div class="context-menu__label">${text}</div>`
          : `
      <button class="context-menu__item dv-body-md" id="vt-copy-link">
        <span class="context-menu__icon dv-icons">file_copy</span>
        <span class="context-menu__text">
          ${this.translate.instant('Map.Legend.CopyToClipboard')}
        </span>
      </button>

      <button class="context-menu__item dv-body-md" id="vt-custom-text">
        <span class="context-menu__icon dv-icons">file_add</span>
        <span class="context-menu__text">
          ${this.translate.instant('Map.Legend.SetCustomText')}
        </span>
      </button>

      <button class="context-menu__item dv-body-md" id="vt-remove-marker">
        <span class="context-menu__icon dv-icons">delete</span>
        <span class="context-menu__text">
          ${this.translate.instant('Map.Legend.RemoveMarker')}
        </span>
      </button>
    `
      }</div>`,
      {
        closeButton: false,
        className: 'leaflet-context-menu',
        autoPan: false,
        offset: L.point(95, 50),
      },
    );
  }

  private openCustomTextDialog(latlng: L.LatLng, map: L.Map): void {
    const dialogRef = this.dialog.open({
      contentComponent: CustomTextDialogComponent,
      title: 'General.EnterCustomText',
      hideFooter: true,
    });

    dialogRef.closed.pipe(take(1)).subscribe((result) => {
      if (result.source === 'confirm') {
        this.updateUrlWithCustomText(latlng, result.responseData, map);
        this.copyUrlToClipboard();
        this.showCopiedToClipboardSnackbar();
      }
    });
  }

  private showCopiedToClipboardSnackbar(): void {
    this.snackbar.open(
      this.translate.instant('General.UrlCopiedToClipboard'),
      SnackBarConfig.Default,
    );
  }

  private openMarkerContextMenu(): void {
    this.dblClickMarker?.openPopup();
  }

  private copyUrlToClipboard(): void {
    navigator.clipboard.writeText(window.location.href);
  }

  private updateUrlWithCustomText(latlng: L.LatLng, customText: string, map: L.Map): void {
    const params = new URLSearchParams(window.location.search);

    params.set('label', customText);
    params.set('lat', latlng.lat.toFixed(5));
    params.set('lng', latlng.lng.toFixed(5));
    params.set('zoom', map.getZoom().toString());

    this.location.replaceState(window.location.pathname, params.toString());
  }

  private getCurrentParams(): URLSearchParams {
    const fullPath = this.location.path(true);
    const queryIndex = fullPath.indexOf('?');

    return new URLSearchParams(queryIndex >= 0 ? fullPath.substring(queryIndex + 1) : '');
  }

  private updateUrlFromMap(map: L.Map, latLng: L.LatLng): void {
    const zoom = map.getZoom();

    const queryParams = {
      lat: latLng.lat.toFixed(5),
      lng: latLng.lng.toFixed(5),
      zoom: zoom,
      label: null,
    };

    this.router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
